import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Copy, Eye, EyeOff, Landmark, Pencil, ShieldCheck } from "lucide-react";
import { useApp } from "../context/AppContext";
import { useAuth, authHeaders, errCode } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BANKS = ["alrajhi", "snb", "riyad", "sab", "alinma", "albilad", "anb", "bsf", "aljazira", "gib", "stc_pay", "other"];

export const normalizeIban = (s) => (s || "").replace(/\s+/g, "").toUpperCase();
export const groupIban = (s) => normalizeIban(s).replace(/(.{4})/g, "$1 ").trim();

export function validateSaIban(raw) {
  const iban = normalizeIban(raw);
  if (!iban) return "IBAN_REQUIRED";
  if (!iban.startsWith("SA")) return "IBAN_NOT_SAUDI";
  if (!/^SA\d{22}$/.test(iban)) return "IBAN_FORMAT";
  const digits = (iban.slice(4) + iban.slice(0, 4)).replace(/[A-Z]/g, (c) => String(c.charCodeAt(0) - 55));
  let rem = 0;
  for (const ch of digits) rem = (rem * 10 + Number(ch)) % 97;
  return rem === 1 ? null : "IBAN_CHECKSUM";
}

export function copyText(text, okMsg) {
  navigator.clipboard?.writeText(text).then(() => toast.success(okMsg)).catch(() => toast.error("—"));
}

export function bankLabel(bank, t) {
  if (!bank) return "";
  return bank.bank_name === "other" ? bank.bank_other || t("bank.other") : t(`bank.name.${bank.bank_name}`);
}

function BankForm({ initial, onSaved, onCancel, t }) {
  const [form, setForm] = useState({ holder: initial?.holder || "", bank_name: initial?.bank_name || "alrajhi", bank_other: initial?.bank_other || "", iban: initial?.iban ? groupIban(initial.iban) : "" });
  const [busy, setBusy] = useState(false);
  const ibanErr = form.iban ? validateSaIban(form.iban) : null;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    if (validateSaIban(form.iban)) return;
    setBusy(true);
    try {
      const { data } = await axios.put(`${API}/payouts/bank`, { ...form, iban: normalizeIban(form.iban) }, { headers: authHeaders() });
      toast.success(t("bank.saved")); onSaved(data.bank);
    } catch (err) { toast.error(t(`err.${errCode(err)}`)); } finally { setBusy(false); }
  };
  return (
    <form className="po-form bank-form" onSubmit={save} data-testid="bank-form">
      <div className="le-grid">
        <label>{t("bank.holder")}<input value={form.holder} onChange={set("holder")} required minLength={2} maxLength={120} data-testid="bank-holder" /></label>
        <label>{t("bank.bank")}
          <select value={form.bank_name} onChange={set("bank_name")} className="an-select bank-select" data-testid="bank-name">
            {BANKS.map((b) => <option key={b} value={b}>{t(`bank.name.${b}`)}</option>)}
          </select>
        </label>
      </div>
      {form.bank_name === "other" && <label>{t("bank.otherName")}<input value={form.bank_other} onChange={set("bank_other")} required maxLength={80} data-testid="bank-other" /></label>}
      <label>IBAN
        <input value={form.iban} onChange={(e) => setForm({ ...form, iban: groupIban(e.target.value) })} dir="ltr" placeholder="SA00 0000 0000 0000 0000 0000" maxLength={29} required
          className={ibanErr ? "bank-input-error" : ""} aria-invalid={!!ibanErr} data-testid="bank-iban" />
        {ibanErr && <span className="bank-error" data-testid="bank-iban-error">{t(`err.${ibanErr}`)}</span>}
        {!ibanErr && form.iban && <span className="bank-ok" data-testid="bank-iban-ok"><ShieldCheck size={12} /> {t("bank.ibanValid")}</span>}
      </label>
      <div className="ba-actions">
        <button type="submit" className="btn-lux-gold" disabled={busy || !!ibanErr || !form.iban} data-testid="bank-save">{t("bank.save")}</button>
        {onCancel && <button type="button" className="btn-lux-ghost" onClick={onCancel} data-testid="bank-cancel">{t("admin.cancel")}</button>}
      </div>
    </form>
  );
}

export default function BankDetailsCard() {
  const { t } = useApp();
  const { refreshUser } = useAuth();
  const [bank, setBank] = useState(undefined);
  const [edit, setEdit] = useState(false);
  const [reveal, setReveal] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/payouts/bank`, { headers: authHeaders() }).then((r) => setBank(r.data.bank)).catch(() => setBank(null));
  }, []);
  useEffect(() => { load(); }, [load]);
  if (bank === undefined) return null;

  return (
    <section className="ba-card glass-card bank-card" data-testid="bank-details-card">
      <div className="ba-head">
        <Landmark size={18} className="ba-ic" />
        <div className="ba-text">
          <h3 className="dash-panel-title ba-title">{t("bank.title")}</h3>
          <p className="ba-desc">{t("bank.desc")}</p>
        </div>
        {bank && !edit && <button className="btn-lux-ghost po-act" onClick={() => setEdit(true)} data-testid="bank-edit"><Pencil size={13} /> {t("wq.edit")}</button>}
      </div>
      {!bank || edit ? (
        <BankForm initial={bank} t={t} onSaved={(b) => { setBank(b); setEdit(false); refreshUser?.(); }} onCancel={bank ? () => setEdit(false) : null} />
      ) : (
        <dl className="bank-view" data-testid="bank-view">
          <div><dt>{t("bank.holder")}</dt><dd data-testid="bank-view-holder">{bank.holder}</dd></div>
          <div><dt>{t("bank.bank")}</dt><dd data-testid="bank-view-bank">{bankLabel(bank, t)}</dd></div>
          <div className="bank-iban-row"><dt>IBAN</dt>
            <dd dir="ltr" className="po-mono" data-testid="bank-view-iban">{reveal ? groupIban(bank.iban) : bank.iban_masked}
              <button type="button" className="bank-icon-btn" onClick={() => setReveal((r) => !r)} aria-label="toggle" data-testid="bank-iban-reveal">{reveal ? <EyeOff size={14} /> : <Eye size={14} />}</button>
              <button type="button" className="bank-icon-btn" onClick={() => copyText(bank.iban, t("bank.copied"))} aria-label="copy" data-testid="bank-iban-copy"><Copy size={14} /></button>
            </dd>
          </div>
        </dl>
      )}
    </section>
  );
}
